/*
 * vms-variant-picker-mcp.js
 * ---------------------------------------------------------------------------
 * [MCP 2026-09-09] A standalone WebMCP enhancement layer for the pipe-length picker on the eight
 * configurable ceiling mounts (product pages that render snippets/vms-variant-picker.liquid). ROUND 3
 * of the page-scoped WebMCP coverage, after the cross-reference lookup (assets/vms-xref-mcp.js) and the
 * ceiling-mount finder (assets/vms-tvcm-mcp.js), and built to the SAME shape on purpose: one vocabulary
 * across the VMS layers.
 *
 * WHAT THIS IS. WebMCP lets a page expose MCP-style tools to an in-browser agent (Chrome built-in AI,
 * Claude in Chrome, Cursor) through a modelContext object. This file registers three tools that read and
 * drive the length / plate picker. It is a ZERO-IMPACT progressive enhancement: with no modelContext
 * present (every normal shopper's browser today) it registers nothing and changes nothing. It also
 * registers nothing on a product whose picker has no LENGTH choice.
 *
 * THE CONTRACT. This file reads ONLY two things: the <script type="application/json" data-vms-vp-data>
 * snapshot the snippet emits inside <variant-selects> (re-rendered fresh on every variant change, so it
 * is read fresh on every call, never cached), and the public window.VMSVariantPickerPage API the
 * snippet's own document-level script sets up (getOptions / getSelection / select / getState). The one
 * mutating tool, select-length, changes ONLY the picker selection, and it does so through the SAME
 * native radio change a shopper's click makes (page().select drives the radio, Dawn re-renders price /
 * SKU / the form's variant id / the URL). NO tool here adds to the cart and NO tool navigates: Shopify's
 * own native storefront tools already handle browse, search, product and cart, so this layer only adds
 * the picker's own rules (the length ranges, the No Mast note and its builder links, the included parts).
 *
 * ROUND 3 REVIEW FIXES (2026-09-09). (1) select-length RE-READS the snapshot after the plate select and
 * re-checks the length's availability against the fresh snapshot: Shopify's per-value available flag is
 * contextual to the currently selected other option, so a length that reads available before the plate
 * change can be sold out after it. (2) On a length number it resolves by the owner's "cheapest adjustable
 * option" rule: among the covering ranges it prefers the CHEAPEST (priced with the current plate) that
 * leaves at least 2 inches of room on both sides of the number, else the cheapest covering range, and it
 * returns a resolved object (requested_in, chosen, reason, also_covers) and a matched_on tag. (3) It
 * accepts a number with a trailing in / inch / inches / quote-mark, an "A to B" range typed without the
 * unit, and a printed fixed value by number (a value within 0.5 inch matches); it refuses feet with the
 * inch conversion. (4) select-length refuses while the page is still busy and, after the change settles,
 * verifies the committed variant actually carries the requested value (Dawn aborts an earlier fetch when
 * a second change fires, so a stale promise must not report someone else's change). (5) Plate aliases
 * (angled/sloped/vaulted/cathedral, unistrut/strut, flat) resolve to the printed plate. (6) Every
 * real dead-end refusal ends with the No Mast route (where it applies) and the phone number.
 *
 * TERMINOLOGY. The customer-facing word for the measurement is LENGTH everywhere in these descriptions,
 * never "drop" (owner terminology rule in the header of assets/vms-configurator.js). Any "drop" text an
 * agent sees comes from stored product notes passed straight through, never from this file. What the
 * printed length is measured to DIFFERS BY PRODUCT (some to the middle of the screen, the dental mount to
 * the back of the TV), so the tools read it from the snapshot's measure block, never hard-code it.
 *
 * ENTRY POINT, the same Chrome 151 facts measured on the shipped sibling layers (WebMCP flag on, dev
 * preview):
 *   - BOTH document.modelContext AND navigator.modelContext exist; we read either.
 *   - The object is INJECTED LATE, after this deferred script evaluates. A one-shot attach at load
 *     therefore silently no-ops and the page reports "no tools". So the auto-attach below is PATIENT:
 *     re-resolve modelContext at each attempt and retry on DOMContentLoaded, window load, then a timer
 *     backoff, giving up after about 31s.
 *   - Shopify's own platform already registers TEN native storefront tools (browse_store, get_cart,
 *     search_catalog, proceed_to_checkout, ...) in the SAME registry; our kebab-case tools coexist
 *     beside those snake_case natives (no name clash, no action needed).
 *   - This modelContext has NO unregisterTool. Nothing here calls or depends on it.
 * Registration is gated to a secure, top-level page and is a silent no-op with no modelContext, so a
 * normal shopper is unaffected. attach() is IDEMPOTENT per target (a WeakSet of attached targets), so
 * retries and a manual second call never double-register. window.VMSVariantPickerMCP = { tools, attach }
 * is exposed so tests and browser verification can drive the same registrations against a stub.
 *
 * TOOL SHAPE. Each tool is { name (kebab-case), description (plain merchant language, inches everywhere),
 * inputSchema (JSON Schema), execute(args) -> { content: [{ type:"text", text:<string> }] } (data is
 * JSON.stringify'd into that text), annotations }. The read tools carry annotations.readOnlyHint = true;
 * select-length carries { readOnlyHint: false, destructiveHint: false, idempotentHint: true } (setting a
 * choice again lands on the same variant). Agent params are treated as UNTRUSTED and validated here
 * before any page call; a bad value is refused with a plain-text reason, never used.
 *
 * [PAGE TOOLS 2026-09-11] The SAME tool objects are also published on window.VMSPageTools (the page-tools
 * registry the storefront chat's page script reads); the registry path calls execute() directly so a
 * failure THROWS, while the WebMCP path shares name / description / inputSchema / annotations / readOnlyHint
 * by reference and wraps ONLY execute so the shim still gets today's error envelope. Registry registration
 * keeps today's hasLengthOption() gate: a product with no length option registers nothing anywhere.
 */
(function () {
  'use strict';

  // [PAGE TOOLS 2026-09-11] Shared page-tools registry (window.VMSPageTools). ONE tool object,
  // TWO consumers: this registry, which the storefront chat's own page script reads and executes,
  // and the WebMCP modelContext attach further down. The block is idempotent and byte-identical in
  // all five VMS MCP assets, so load order and double-inclusion are harmless. Registry registration
  // is UNGATED (it runs in every browser and does not wait for modelContext); the WebMCP attach
  // stays feature-gated as before. DEVIATION FROM "one shared object reference": the registry holds
  // the tool object whose execute() THROWS on failure; the WebMCP path shares that object's name,
  // description, inputSchema, annotations and readOnlyHint by reference but wraps ONLY execute, so
  // the shim still gets today's error envelope (see adaptExecute below).
  var VMS_PAGE_TOOLS = (typeof window !== 'undefined' && window)
    ? (window.VMSPageTools = window.VMSPageTools || { version: 1, tools: [] })
    : null;
  if (VMS_PAGE_TOOLS && !VMS_PAGE_TOOLS.register) {
    VMS_PAGE_TOOLS.register = function (tool) {
      if (!tool || typeof tool.name !== 'string') return;
      var idx = -1;
      for (var i = 0; i < VMS_PAGE_TOOLS.tools.length; i++) { if (VMS_PAGE_TOOLS.tools[i].name === tool.name) { idx = i; break; } }
      if (idx >= 0) VMS_PAGE_TOOLS.tools[idx] = tool; else VMS_PAGE_TOOLS.tools.push(tool);
      try { document.dispatchEvent(new CustomEvent('vms:page-tools-changed')); } catch (e) { /* old browser or no document: the registration still stands */ }
    };
    VMS_PAGE_TOOLS.unregister = function (name) {
      var before = VMS_PAGE_TOOLS.tools.length;
      var kept = [];
      for (var i = 0; i < VMS_PAGE_TOOLS.tools.length; i++) { if (VMS_PAGE_TOOLS.tools[i].name !== name) kept.push(VMS_PAGE_TOOLS.tools[i]); }
      VMS_PAGE_TOOLS.tools = kept;
      if (VMS_PAGE_TOOLS.tools.length !== before) { try { document.dispatchEvent(new CustomEvent('vms:page-tools-changed')); } catch (e) { /* no document: ignore */ } }
    };
    VMS_PAGE_TOOLS.list = function () { return VMS_PAGE_TOOLS.tools.slice(); };
  }

  var ORIGIN = 'https://videomountstore.com';
  var PHONE = '800-585-8795';

  // MCP tool result: a content array with one text part. Objects are JSON.stringify'd (agents read the text).
  function textResult(payload) {
    var text = (typeof payload === 'string') ? payload : JSON.stringify(payload);
    return { content: [{ type: 'text', text: text }] };
  }

  // A snapshot is usable only when it carries the shapes every tool reads: an options[] array, a
  // variants[] array, and a product object. Anything else is treated as ABSENT so a tool reports the
  // plain not-on-this-page error instead of throwing on undefined.
  function validShape(d) {
    return !!d && typeof d === 'object' && Array.isArray(d.options) && Array.isArray(d.variants)
      && !!d.product && typeof d.product === 'object';
  }

  // DATA ACCESS. Read FRESH on every call, never cached: Dawn replaces the whole <variant-selects> on
  // every variant change, so the data-vms-vp-data block is re-rendered and a cached copy would go stale.
  // Prefer a page-provided window.VMSVariantPickerData object (tests inject it) when well shaped;
  // otherwise parse the FIRST [data-vms-vp-data] block in the DOM. Returns null when neither yields a
  // well-shaped snapshot, and every tool then reports it plainly.
  function data() {
    if (typeof window !== 'undefined' && validShape(window.VMSVariantPickerData)) return window.VMSVariantPickerData;
    try {
      var doc = (typeof window !== 'undefined' && window.document) || ((typeof document !== 'undefined') ? document : null);
      var el = (doc && typeof doc.querySelector === 'function') ? doc.querySelector('[data-vms-vp-data]') : null;
      var txt = el ? (el.textContent || el.innerHTML || '') : '';
      if (txt) { var parsed = JSON.parse(txt); if (validShape(parsed)) return parsed; }
    } catch (e) { /* fall through to null */ }
    return null;
  }

  // The picker's public page API (snippets/vms-variant-picker.liquid). Present only when the snippet's
  // own script ran. select-length REQUIRES it; the read tools fall back to the snapshot when it is absent.
  function page() { return (typeof window !== 'undefined' && window.VMSVariantPickerPage) || null; }

  // [PAGE TOOLS 2026-09-11] A missing snapshot / not-ready page THROWS a clear customer-safe message
  // (never a partial or fabricated result). On the WebMCP path adaptExecute turns the throw back into
  // the error envelope; on the registry path the throw reaches the chat's page script as a rejection.
  function noData() { throw new Error('The variant picker data is not on this page.'); }

  // [PAGE TOOLS 2026-09-11] WebMCP execute adapter. On the registry path the chat's page script
  // calls a tool's execute() directly, so a failure must THROW (a synchronous throw or a rejected
  // Promise) for the chat to tell failure from success. The WebMCP shim, though, expects today's
  // error ENVELOPE, so here (and ONLY here) a thrown or rejected execute is turned back into
  // textResult(message). A successful execute (a sync envelope, or a Promise of one) passes through
  // unchanged, so a read-only tool stays synchronous for the shim.
  function errMessage(e) { return (e && e.message) ? e.message : 'This tool could not run right now.'; }
  function adaptExecute(t) {
    return function (input) {
      try {
        var out = t.execute(input);
        if (out && typeof out.then === 'function') { return out.then(function (r) { return r; }, function (e) { return textResult(errMessage(e)); }); }
        return out;
      } catch (e) { return textResult(errMessage(e)); }
    };
  }

  function absUrl(u) { return (typeof u === 'string' && u.indexOf('/') === 0) ? ORIGIN + u : u; }
  // [MCP 2026-09-09] Round cents to whole cents before dividing, so 12995 cents reads back as 129.95,
  // never 129.94999999. delta_cents is an integer already; this guards a non-integer that slips in.
  function centsToDollars(c) { return (typeof c === 'number' && isFinite(c)) ? (Math.round(c) / 100) : 0; }
  function formatPrice(n) { return (typeof n === 'number' && isFinite(n)) ? n.toFixed(2) : String(n); }

  function optByRole(d, role) {
    for (var i = 0; i < d.options.length; i++) { if (d.options[i].role === role) return d.options[i]; }
    return null;
  }
  function findValueObj(opt, value) {
    if (!opt) return null;
    for (var i = 0; i < opt.values.length; i++) { if (opt.values[i].value === value) return opt.values[i]; }
    return null;
  }

  // The currently selected value for one option: prefer the live page selection (getSelection().options
  // is indexed by option position), then the snapshot's selected flag, else null.
  function selectedValue(d, opt, sel) {
    if (!opt) return null;
    if (sel && Array.isArray(sel.options)) {
      var v = sel.options[opt.position - 1];
      if (v != null) return v;
    }
    for (var i = 0; i < opt.values.length; i++) { if (opt.values[i].selected) return opt.values[i].value; }
    return null;
  }

  // The snapshot variant matching a set of { position: value } constraints (only the constrained
  // positions are tested), first match wins.
  function variantByConstraints(d, cons) {
    for (var i = 0; i < d.variants.length; i++) {
      var v = d.variants[i];
      var ok = true;
      for (var pos in cons) {
        if (!Object.prototype.hasOwnProperty.call(cons, pos)) continue;
        if (cons[pos] == null) continue;
        if (v['option' + pos] !== cons[pos]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  // The absolute price (dollars) of the variant that combines a given length value with the currently
  // selected plate (and any currently selected third option), or null if none exists.
  function priceForCombo(d, lenOpt, lenValue, plateOpt, selPlate, sel) {
    var cons = {};
    for (var i = 0; i < d.options.length; i++) {
      var opt = d.options[i];
      if (opt.position === lenOpt.position) { cons[opt.position] = lenValue; }
      else if (plateOpt && opt.position === plateOpt.position) { cons[opt.position] = selPlate; }
      else { cons[opt.position] = selectedValue(d, opt, sel); }
    }
    var v = variantByConstraints(d, cons);
    return v ? v.price : null;
  }

  // The absolute price (dollars) of the variant that combines a given plate value with the CURRENT
  // length (and any currently selected third option), or null if none exists.
  function priceForPlateCombo(d, plateOpt, plateValue, lenOpt, selLen, sel) {
    var cons = {};
    for (var i = 0; i < d.options.length; i++) {
      var opt = d.options[i];
      if (opt.position === plateOpt.position) { cons[opt.position] = plateValue; }
      else if (lenOpt && opt.position === lenOpt.position) { cons[opt.position] = selLen; }
      else { cons[opt.position] = selectedValue(d, opt, sel); }
    }
    var v = variantByConstraints(d, cons);
    return v ? v.price : null;
  }

  // The first variant carrying a given plate value, mapped to its plate_component (sku, title, url), or null.
  function plateComponentFor(d, plateOpt, plateValue) {
    for (var i = 0; i < d.variants.length; i++) {
      if (d.variants[i]['option' + plateOpt.position] === plateValue) return mapComponent(d.variants[i].plate_component);
    }
    return null;
  }

  function selectedVariant(d, sel) {
    if (!d) return null;
    var id = (sel && sel.variant_id != null) ? sel.variant_id : d.selected_variant_id;
    for (var i = 0; i < d.variants.length; i++) { if (d.variants[i].id === id) return d.variants[i]; }
    return null;
  }

  function mapComponent(c) {
    if (!c) return null;
    return { sku: c.sku, title: c.title, url: absUrl(c.url) };
  }
  function includedFor(v) {
    if (!v) return { plate_component: null, length_components: [] };
    return {
      plate_component: mapComponent(v.plate_component),
      length_components: (v.length_components || []).map(mapComponent)
    };
  }

  // Parse a length value printed as "A to B ..." (inches). Returns { lo, hi } or null.
  function parseRange(s) {
    var m = String(s).match(/^\s*(\d+(?:\.\d+)?)\s+to\s+(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    return { lo: parseFloat(m[1]), hi: parseFloat(m[2]) };
  }
  function rangeList(values) {
    var out = [];
    for (var i = 0; i < values.length; i++) { if (parseRange(values[i].value)) out.push(values[i].value); }
    return out;
  }

  // Parse an agent's number-of-inches string: strips a trailing in / inch / inches / quote mark, and
  // reads a mixed fraction ("6 1/2"), a simple fraction ("1/2") or a decimal. Returns a number or null.
  // A range ("28 to 54") is NOT a number and returns null (the caller routes it to the prefix match).
  function parseInches(raw) {
    var s = String(raw).trim().toLowerCase();
    s = s.replace(/\s*(?:inches|inch|in|")\s*$/, '').trim();
    if (s === '') return null;
    var mf = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mf) { var d1 = parseFloat(mf[3]); return d1 ? (parseFloat(mf[1]) + parseFloat(mf[2]) / d1) : null; }
    var sf = s.match(/^(\d+)\/(\d+)$/);
    if (sf) { var d2 = parseFloat(sf[2]); return d2 ? (parseFloat(sf[1]) / d2) : null; }
    if (/^\d+(?:\.\d+)?$/.test(s)) return parseFloat(s);
    return null;
  }

  // The inches a PRINTED fixed value stands for: "6 1/2 Inch length" -> 6.5, "24 inch" / "24 Inches" -> 24.
  // A range ("A to B") is not fixed and returns null.
  function fixedInches(valueStr) {
    var t = String(valueStr).trim().toLowerCase();
    if (t.indexOf(' to ') !== -1) return null;
    var mf = t.match(/^(\d+)\s+(\d+)\/(\d+)\b/);
    if (mf) { var d1 = parseFloat(mf[3]); return d1 ? (parseFloat(mf[1]) + parseFloat(mf[2]) / d1) : null; }
    var m2 = t.match(/^(\d+(?:\.\d+)?)\b/);
    if (m2) return parseFloat(m2[1]);
    return null;
  }
  function fixedList(values) {
    var out = [];
    for (var i = 0; i < values.length; i++) { if (!parseRange(values[i].value) && fixedInches(values[i].value) != null) out.push(values[i].value); }
    return out;
  }

  function hasNoMast(values) {
    for (var i = 0; i < values.length; i++) { if (values[i] && values[i].is_no_mast) return true; }
    return false;
  }
  // The safety net appended to every REAL dead-end refusal (no covering range, unavailable, or the page
  // could not update): the No Mast route where the product offers it, then the phone number.
  function deadEndTail(values) {
    var t = '';
    if (values && hasNoMast(values)) {
      t += ' Or pick No Mast and add your own 1-1/2 inch NPT pipe; the build link in get-length-options opens the builder.';
    }
    t += ' Call ' + PHONE + ' and we will help.';
    return t;
  }

  // Resolve an agent's length argument to a printed option value, with the owner's "cheapest adjustable
  // option" rule for a number. Accepts (in order): an exact printed value, a case-insensitive printed
  // value, a number of inches (a JSON number, or a string with an optional in / inch / inches / quote
  // unit, or a fraction), an "A to B" range typed without the unit (a case-insensitive prefix). Refuses
  // feet with the inch conversion. Returns { value, matched_on } (with a resolved object for a number /
  // fixed match) or { error }.
  function resolveLength(d, lenOpt, plateOpt, selPlate, sel, arg) {
    var values = lenOpt.values;

    function priceOfValue(val) { return priceForCombo(d, lenOpt, val, plateOpt, selPlate, sel); }
    function rangesStr() { var r = rangeList(values); return r.length ? r.join(', ') : '(none)'; }
    function fixedStr() { var f = fixedList(values); return f.length ? f.join(', ') : '(none)'; }
    function numberDeadEnd(num) {
      return 'No length range on this page covers ' + num + ' inches. The adjustable ranges are: ' + rangesStr()
        + '. The fixed lengths are: ' + fixedStr() + '.' + deadEndTail(values);
    }

    function resolveNumber(num) {
      // covering adjustable ranges, priced with the current plate
      var covering = [];
      for (var k = 0; k < values.length; k++) {
        var rng = parseRange(values[k].value);
        if (rng && num >= rng.lo && num <= rng.hi) {
          covering.push({ value: values[k].value, lo: rng.lo, hi: rng.hi, available: values[k].available !== false, price: priceOfValue(values[k].value) });
        }
      }
      if (covering.length) {
        var avail = covering.filter(function (c) { return c.available; });
        if (!avail.length) {
          var names = covering.map(function (c) { return '"' + c.value + '"'; }).join(', ');
          return { error: 'The length range(s) that cover ' + num + ' inches (' + names + ') are not available on this mount right now.' + deadEndTail(values) };
        }
        function priceKey(c) { return (typeof c.price === 'number' && isFinite(c.price)) ? c.price : Infinity; }
        // at least 2 inches of room on BOTH sides of the number
        var roomOk = avail.filter(function (c) { return (num - c.lo) >= 2 && (c.hi - num) >= 2; });
        var pool = (roomOk.length ? roomOk : avail).slice().sort(function (a, b) { return priceKey(a) - priceKey(b); });
        var chosen = pool[0];
        var others = avail.filter(function (c) { return c.value !== chosen.value; })
          .sort(function (a, b) { return priceKey(a) - priceKey(b); })
          .map(function (c) { return { value: c.value, price: (c.price != null ? c.price : null) }; });
        var below = Math.round((num - chosen.lo) * 10) / 10;
        var above = Math.round((chosen.hi - num) * 10) / 10;
        var reason;
        if (roomOk.length) {
          reason = '"' + chosen.value + '" covers ' + num + ' inches with ' + below + ' inches of room below and ' + above
            + ' inches above, and it is the cheapest covering length with at least 2 inches of room on both sides.';
        } else {
          reason = '"' + chosen.value + '" is the cheapest length that covers ' + num
            + ' inches; none of the covering lengths leaves 2 inches of room on both sides of ' + num + ' inches.';
        }
        return { value: chosen.value, matched_on: 'number', resolved: { requested_in: num, chosen: chosen.value, reason: reason, also_covers: others } };
      }
      // printed fixed values within 0.5 inch of the number
      var fixedHits = [];
      for (var j = 0; j < values.length; j++) {
        var fx = fixedInches(values[j].value);
        if (fx != null && Math.abs(num - fx) <= 0.5) fixedHits.push({ value: values[j].value, fx: fx, available: values[j].available !== false });
      }
      if (fixedHits.length) {
        fixedHits.sort(function (a, b) { return Math.abs(num - a.fx) - Math.abs(num - b.fx); });
        var pick = fixedHits[0];
        if (!pick.available) { return { error: '"' + pick.value + '" is not available on this mount right now.' + deadEndTail(values) }; }
        return { value: pick.value, matched_on: 'fixed', resolved: { requested_in: num, chosen: pick.value, reason: num + ' inches matches the fixed length "' + pick.value + '".', also_covers: [] } };
      }
      return { error: numberDeadEnd(num) };
    }

    if (typeof arg === 'number' && isFinite(arg)) { return resolveNumber(arg); }
    if (typeof arg !== 'string') {
      return { error: 'The length must be text (an option value as printed, for example "28 to 44 inches" or "No Mast") or a number of inches.' };
    }
    var t = arg.trim();
    if (t.length < 1 || t.length > 60) {
      return { error: 'The length must be 1 to 60 characters: an option value as printed (for example "28 to 44 inches" or "No Mast") or a number of inches.' };
    }
    // feet are refused with the inch conversion (never guessed at)
    var ft = t.match(/^(\d+(?:\.\d+)?)\s*(?:feet|foot|ft|')$/i);
    if (ft) {
      var fnum = parseFloat(ft[1]);
      return { error: 'Give the length in inches; ' + fnum + ' feet is ' + (fnum * 12) + ' inches.' };
    }
    var i;
    for (i = 0; i < values.length; i++) { if (values[i].value === t) return { value: values[i].value, matched_on: 'exact' }; }
    var lc = t.toLowerCase();
    for (i = 0; i < values.length; i++) { if (String(values[i].value).toLowerCase() === lc) return { value: values[i].value, matched_on: 'case-insensitive' }; }
    var inches = parseInches(t);
    if (inches != null) { return resolveNumber(inches); }
    // "A to B" typed without the unit: a case-insensitive prefix of exactly one printed value
    if (lc.indexOf(' to ') !== -1) {
      var pref = [];
      for (i = 0; i < values.length; i++) { if (String(values[i].value).toLowerCase().indexOf(lc) === 0) pref.push(values[i].value); }
      if (pref.length === 1) return { value: pref[0], matched_on: 'prefix' };
    }
    return { error: '"' + arg + '" is not a length choice on this page. The choices are: ' + values.map(function (v) { return v.value; }).join(', ') + '.' };
  }

  // Resolve an agent's plate argument to a printed plate value. An exact or case-insensitive printed
  // value always wins; otherwise a small set of aliases maps to the value containing the keyword.
  function resolvePlate(plateOpt, arg) {
    var values = plateOpt.values, i;
    var raw = String(arg);
    for (i = 0; i < values.length; i++) { if (values[i].value === raw) return { value: values[i].value }; }
    var lc = raw.toLowerCase().trim();
    for (i = 0; i < values.length; i++) { if (String(values[i].value).toLowerCase() === lc) return { value: values[i].value }; }
    var aliases = [
      { keys: ['angled', 'sloped', 'vaulted', 'cathedral'], contains: 'angled' },
      { keys: ['unistrut', 'strut'], contains: 'unistrut' },
      { keys: ['flat'], contains: 'flat' }
    ];
    for (var a = 0; a < aliases.length; a++) {
      if (aliases[a].keys.indexOf(lc) !== -1) {
        for (i = 0; i < values.length; i++) {
          if (String(values[i].value).toLowerCase().indexOf(aliases[a].contains) !== -1) return { value: values[i].value };
        }
      }
    }
    return { error: '"' + arg + '" is not a plate choice on this page. The choices are: ' + values.map(function (v) { return v.value; }).join(', ') + '.' };
  }

  function noMastBlock(d) {
    if (!d.no_mast) return null;
    return {
      note: d.no_mast.note || '',
      shop_href: absUrl(d.no_mast.shop_href),
      shop_label: d.no_mast.shop_label || '',
      build_href: d.no_mast.build_href ? absUrl(d.no_mast.build_href) : null
    };
  }

  // The measurement definition, read from the snapshot's measure block (the SAME block settings the
  // how-to-measure modal renders). measured_to says what the printed length is measured to and DIFFERS
  // by product, so the tools never hard-code it. Returns null when the snapshot carries no measure block.
  function measureBlock(d) {
    var m = d && d.measure;
    if (!m || typeof m !== 'object') return null;
    return {
      heading: (m.heading != null) ? m.heading : null,
      body: (m.body != null) ? m.body : null,
      measured_to: (m.measured_to != null) ? m.measured_to : null,
      link_href: m.link_href ? absUrl(m.link_href) : null,
      link_label: (m.link_label != null) ? m.link_label : null,
      hint: (m.hint != null) ? m.hint : null
    };
  }

  // The current selection object shared by select-length and get-selection: the variant, the chosen
  // plate and length values, the included parts, the measurement definition, and (when No Mast is
  // selected) the note and links.
  function currentSelectionObj() {
    var d = data();
    if (!d) return null;
    var p = page();
    var sel = (p && typeof p.getSelection === 'function') ? p.getSelection() : null;
    var lenOpt = optByRole(d, 'length');
    var plateOpt = optByRole(d, 'plate');
    var v = selectedVariant(d, sel);
    var plateValue = plateOpt ? selectedValue(d, plateOpt, sel) : null;
    var lengthValue = lenOpt ? selectedValue(d, lenOpt, sel) : null;
    var out = {
      selection: {
        variant_id: sel ? sel.variant_id : (v ? v.id : null),
        sku: sel ? sel.sku : (v ? v.sku : null),
        title: sel ? sel.title : (v ? v.title : null),
        price: sel ? sel.price : (v ? v.price : null),
        available: sel ? sel.available : (v ? v.available : null),
        url: sel && sel.url ? absUrl(sel.url) : (v ? absUrl(v.url) : null),
        plate: plateValue,
        length: lengthValue,
        pending_mismatch: sel ? !!sel.pending_mismatch : false
      },
      included: includedFor(v),
      measure: measureBlock(d)
    };
    var lenValObj = findValueObj(lenOpt, lengthValue);
    if (lenValObj && lenValObj.is_no_mast) {
      var nm = noMastBlock(d);
      if (nm) out.no_mast = nm;
    }
    return out;
  }

  function selectionHandback(lenVal, price) {
    if (lenVal == null) return 'No length is selected yet.';
    if (price == null) return 'The selected length is "' + lenVal + '".';
    return 'The selected length is "' + lenVal + '" and this mount is $' + formatPrice(price) + '.';
  }

  var tools = [
    {
      name: 'vms-get-length-options',
      annotations: { readOnlyHint: true },
      readOnlyHint: true,
      description: 'List the pipe-length choices this configurable ceiling mount offers, with each price, so you can help the shopper pick. The customer-facing word is LENGTH; read measure for what the printed range is measured to (it differs by product). Returns the product, the ceiling-plate choices with their prices, the measure block, and the LENGTH choices in page order with the no-mast, popular and shortest-fixed flags and the No Mast note. Read-only.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      execute: function () {
        var d = data(); if (!d) return noData();
        var lenOpt = optByRole(d, 'length');
        if (!lenOpt) throw new Error('This product has no length choice.');
        var plateOpt = optByRole(d, 'plate');
        var p = page();
        var computedFrom = (p && typeof p.getSelection === 'function') ? 'page' : 'snapshot';
        var sel = (p && typeof p.getSelection === 'function') ? p.getSelection() : null;
        var selPlate = plateOpt ? selectedValue(d, plateOpt, sel) : null;
        var selLen = selectedValue(d, lenOpt, sel);

        var plates = plateOpt ? plateOpt.values.map(function (val) {
          var combo = priceForPlateCombo(d, plateOpt, val.value, lenOpt, selLen, sel);
          return {
            value: val.value,
            available: !!val.available,
            selected: !!val.selected,
            delta_dollars: centsToDollars(val.delta_cents),
            plate_component: plateComponentFor(d, plateOpt, val.value),
            price: (combo != null) ? combo : null
          };
        }) : [];

        var lengths = lenOpt.values.map(function (val) {
          var combo = priceForCombo(d, lenOpt, val.value, plateOpt, selPlate, sel);
          var o = {
            value: val.value,
            available: !!val.available,
            selected: !!val.selected,
            delta_dollars: centsToDollars(val.delta_cents),
            price: (combo != null) ? combo : null,
            is_no_mast: !!val.is_no_mast,
            is_popular: !!val.is_popular,
            is_min_length: !!val.is_min_length
          };
          if (val.is_no_mast) {
            var nm = noMastBlock(d);
            if (nm) { o.no_mast_note = nm.note; o.shop_href = nm.shop_href; o.shop_label = nm.shop_label; o.build_href = nm.build_href; }
          }
          return o;
        });

        var selVar = selectedVariant(d, sel);
        var selPrice = selVar ? selVar.price : null;

        return textResult({
          product: { handle: d.product.handle, title: d.product.title, url: absUrl(d.product.url) },
          computed_from: computedFrom,
          plates: plates,
          lengths: lengths,
          measure: measureBlock(d),
          min_length_note: d.min_length_note || null,
          selected_length: selLen,
          selected_price: selPrice,
          handback: selectionHandback(selLen, selPrice)
        });
      }
    },
    {
      name: 'vms-select-length',
      annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: true },
      description: 'Set the pipe length (and optionally the plate) on this configurable ceiling mount, like a chip click: nothing is added to the cart and it does not navigate (Dawn only rewrites ?variant). Pass length as a printed option value or a number of inches as a string; a number picks the cheapest adjustable range that covers it. Optionally pass a plate value or alias. An uncovered length, an unknown or unavailable value, or feet is refused. Returns the new selection, matched_on and the included parts.',
      inputSchema: {
        type: 'object',
        properties: {
          length: { type: 'string', description: 'Required. A length option value as printed (for example "28 to 44 inches" or "No Mast"), or a number of inches sent as a string (for example "36") which picks the cheapest adjustable range that leaves room around it. A trailing in / inch / inches or a quote mark is accepted; feet are refused. 1 to 60 characters. (A JSON number is also accepted for hosts that do not validate the type.)' },
          plate: { type: 'string', description: 'Optional. A ceiling-plate value as printed (for example "Flat Ceiling") or an alias (angled, sloped, vaulted, cathedral, unistrut, strut, flat). When given it is set first and the length availability is re-checked against it. Leave it out to keep the current plate.' }
        },
        required: ['length'],
        additionalProperties: false
      },
      execute: function (args) {
        var d = data(); if (!d) return noData();
        var lenOpt = optByRole(d, 'length');
        if (!lenOpt) throw new Error('This product has no length choice.');
        var plateOpt = optByRole(d, 'plate');
        var p = page();
        if (!p || typeof p.select !== 'function') {
          throw new Error('This page cannot change the selection right now (the picker did not load). Nothing was changed.' + deadEndTail(lenOpt.values));
        }
        // re-entrancy guard: refuse while the page is still applying the previous choice
        if (typeof p.getState === 'function') {
          var st = p.getState();
          if (st && st.busy) { throw new Error('The page is still updating the previous choice, try again in a moment.'); }
        }
        var a = args || {};
        if (a.length == null) {
          throw new Error('A length is required. Pass an option value as printed (for example "28 to 44 inches" or "No Mast") or a number of inches.');
        }

        var selInit = (typeof p.getSelection === 'function') ? p.getSelection() : null;
        var selPlateInit = plateOpt ? selectedValue(d, plateOpt, selInit) : null;

        // Early resolve (against the current snapshot) so genuinely bad input is refused BEFORE any page
        // mutation. When a plate is passed we skip the early availability check: the per-value available
        // flag is contextual to the plate, so it is re-checked after the plate select, not against the
        // current plate.
        var lrEarly = resolveLength(d, lenOpt, plateOpt, selPlateInit, selInit, a.length);
        if (lrEarly.error) throw new Error(lrEarly.error);

        var plateValue = null;
        if (a.plate != null) {
          if (typeof a.plate !== 'string' || a.plate.trim() === '') {
            throw new Error('The plate must be a non-empty option value as printed (or an alias), or leave it out to keep the current plate.');
          }
          if (!plateOpt) throw new Error('This product has no plate choice.');
          var pr = resolvePlate(plateOpt, a.plate.trim());
          if (pr.error) throw new Error(pr.error);
          plateValue = pr.value;
          var plateValObj = findValueObj(plateOpt, plateValue);
          if (plateValObj && plateValObj.available === false) {
            throw new Error('"' + plateValue + '" is not available on this mount right now.' + deadEndTail(lenOpt.values));
          }
        } else {
          // no plate change: the early availability check IS meaningful against the current plate
          var lenValObjEarly = findValueObj(lenOpt, lrEarly.value);
          if (lenValObjEarly && lenValObjEarly.available === false) {
            throw new Error('"' + lrEarly.value + '" is not available on this mount right now.' + deadEndTail(lenOpt.values));
          }
        }

        var finalLength = null, finalPlate = plateValue, matchedOn = null, resolvedObj = null;

        var chain = Promise.resolve();
        if (plateValue !== null) { chain = chain.then(function () { return p.select('plate', plateValue); }); }

        chain = chain.then(function () {
          // [MCP 2026-09-09 fix 1] Re-read the snapshot after the plate select: availability (and the
          // cheapest-with-room resolution's prices) are contextual to the now-selected plate.
          var d2 = data() || d;
          var lenOpt2 = optByRole(d2, 'length') || lenOpt;
          var plateOpt2 = optByRole(d2, 'plate');
          var sel2 = (typeof p.getSelection === 'function') ? p.getSelection() : null;
          var selPlate2 = plateOpt2 ? selectedValue(d2, plateOpt2, sel2) : null;
          var lr2 = resolveLength(d2, lenOpt2, plateOpt2, selPlate2, sel2, a.length);
          if (lr2.error) { return Promise.reject(lr2.error); }
          var lenValObj2 = findValueObj(lenOpt2, lr2.value);
          if (lenValObj2 && lenValObj2.available === false) {
            var pn = (plateValue !== null) ? plateValue : selPlate2;
            return Promise.reject('"' + lr2.value + '" is not available' + (pn ? ' with the ' + pn + ' plate' : '') + ' on this mount right now.' + deadEndTail(lenOpt2.values));
          }
          finalLength = lr2.value; matchedOn = lr2.matched_on; resolvedObj = lr2.resolved || null;
          return p.select('length', finalLength);
        });

        chain = chain.then(function () {
          // [MCP 2026-09-09 fix 3] Verify the committed variant actually carries the requested value.
          // Dawn aborts an earlier fetch when a second change fires, so the resolved promise could be
          // reporting a different, interrupting change. Read the fresh selection and compare by position.
          var d3 = data() || d;
          var lenOpt3 = optByRole(d3, 'length') || lenOpt;
          var plateOpt3 = optByRole(d3, 'plate');
          var sel3 = (typeof p.getSelection === 'function') ? p.getSelection() : null;
          var gotLen = (sel3 && Array.isArray(sel3.options)) ? sel3.options[lenOpt3.position - 1] : null;
          if (gotLen !== finalLength) {
            return Promise.reject('The page updated to a different length ("' + (gotLen == null ? 'unknown' : gotLen) + '") than the one requested ("' + finalLength + '"); another change may have interrupted. Please read the current selection and try again.' + deadEndTail(lenOpt3.values));
          }
          if (finalPlate !== null && plateOpt3) {
            var gotPlate = (sel3 && Array.isArray(sel3.options)) ? sel3.options[plateOpt3.position - 1] : null;
            if (gotPlate !== finalPlate) {
              return Promise.reject('The page did not apply the ' + finalPlate + ' plate (it shows "' + (gotPlate == null ? 'unknown' : gotPlate) + '"). Please read the current selection and try again.' + deadEndTail(lenOpt3.values));
            }
          }
          var cur = currentSelectionObj();
          var out = { ok: true };
          if (cur) { out.selection = cur.selection; out.included = cur.included; out.measure = cur.measure; if (cur.no_mast) out.no_mast = cur.no_mast; }
          out.matched_on = matchedOn;
          if (resolvedObj) out.resolved = resolvedObj;
          var vid = (cur && cur.selection) ? cur.selection.variant_id : null;
          out.handback = 'The length is set on the page like a click. Dawn has rewritten the page URL to ?variant=' + (vid == null ? '' : vid)
            + ' for this variant, so selection.url reflects it. Relay what changed to your user. Nothing was added to the cart; the Add to cart button now carries this variant.';
          return textResult(out);
        });

        return chain.catch(function (reason) {
          var msg = (typeof reason === 'string') ? reason : 'The selection could not be changed.';
          // Ensure a page-update failure relayed from the snippet still carries the safety net.
          if (msg.indexOf(PHONE) === -1) {
            var dd = data();
            var lo = dd ? optByRole(dd, 'length') : null;
            msg += deadEndTail(lo ? lo.values : []);
          }
          throw new Error(msg);
        });
      }
    },
    {
      name: 'vms-get-selection',
      annotations: { readOnlyHint: true },
      readOnlyHint: true,
      description: 'Get the current selection on this configurable ceiling mount: the variant id, SKU, title, price, availability and link, the chosen ceiling plate and pipe length, whether a change is still in flight (pending_mismatch), the parts included with the variant, the measurement definition (measure), and, when No Mast is the chosen length, its note and the shop and build links. Read-only; nothing on the page changes.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      execute: function () {
        var d = data(); if (!d) return noData();
        var cur = currentSelectionObj();
        if (!cur) return noData();
        cur.handback = selectionHandback(cur.selection.length, cur.selection.price);
        return textResult(cur);
      }
    }
  ];

  // Register only when the snapshot carries a LENGTH-role option; a product without one is not a
  // configurable mount this layer covers, so it registers nothing.
  function hasLengthOption() {
    var d = data();
    if (!d) return false;
    for (var i = 0; i < d.options.length; i++) { if (d.options[i].role === 'length') return true; }
    return false;
  }

  // [PAGE TOOLS 2026-09-11] Publish the shared tool objects on the registry ONLY when the product has a
  // length option (the same gate the WebMCP attach uses); a product without one registers nothing
  // anywhere. Ungated on modelContext and on the page object, in the listed order.
  if (VMS_PAGE_TOOLS && hasLengthOption()) { for (var pti = 0; pti < tools.length; pti++) { VMS_PAGE_TOOLS.register(tools[pti]); } }

  // [MCP 2026-09-09] Register every tool against an MC-like object (the real modelContext or a test
  // stub). IDEMPOTENT PER TARGET: a target already registered against is skipped (a duplicate
  // registerTool may throw InvalidStateError in Chrome 151), so retries and a manual second call never
  // double-register. Each registerTool is wrapped in its own try, so one tool the host rejects does not
  // abort the rest; the count of successes is returned. A DIFFERENT target (a test stub) is always
  // attempted. unregisterTool is never called (Chrome 151's modelContext has no such method).
  var attachedTargets = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
  function attach(mc) {
    if (!mc || typeof mc.registerTool !== 'function') return 0;
    if (!hasLengthOption()) return 0;   // nothing to expose on a product without a length choice
    if (attachedTargets && attachedTargets.has(mc)) return 0;   // idempotent: already attached to this target
    var n = 0;
    for (var i = 0; i < tools.length; i++) {
      var t = tools[i];
      try {
        // [PAGE TOOLS 2026-09-11] Share name / description / inputSchema / annotations by reference and
        // wrap ONLY execute so the shim keeps getting today's error envelope on a failure. readOnlyHint is
        // added only when true, never as an explicit undefined on a mutating tool.
        var reg = { name: t.name, description: t.description, inputSchema: t.inputSchema, annotations: t.annotations, execute: adaptExecute(t) };
        if (t.readOnlyHint === true) reg.readOnlyHint = true;
        if (t.outputSchema) reg.outputSchema = t.outputSchema;
        mc.registerTool(reg);
        n++;
      } catch (e) { /* a tool the host rejects (or already-registered): keep the rest */ }
    }
    if (n > 0 && attachedTargets) attachedTargets.add(mc);   // mark attached only after a successful pass
    return n;
  }

  if (typeof window !== 'undefined') {
    window.VMSVariantPickerMCP = { tools: tools, attach: attach };
    // [MCP 2026-09-09] PATIENT, IDEMPOTENT AUTO-ATTACH, copied in behaviour from the shipped sibling
    // layers. Chrome injects modelContext AFTER this deferred script evaluates, so the old one-shot
    // attach at load silently no-opped and the page reported "no tools". This modelContext also has NO
    // unregisterTool. So: re-resolve document/navigator.modelContext at EACH attempt (never cache a
    // null), and retry on DOMContentLoaded, on window load, then on a timer backoff, giving up after
    // about 31s. The whole thing is gated to a secure, top-level page and is a silent no-op with no
    // modelContext, so a normal shopper's browser is completely unaffected. Reads window.document /
    // window.navigator (identical to the bare globals in a browser) so a test can inject a late
    // modelContext.
    var done = false;
    function currentMC() {
      var doc = (window.document) || ((typeof document !== 'undefined') ? document : null);
      var nav = (window.navigator) || ((typeof navigator !== 'undefined') ? navigator : null);
      return (doc && doc.modelContext) || (nav && nav.modelContext) || null;
    }
    function tryAttachNow() {
      if (done) return true;
      var topLevel = (window.top === window.self);
      var secure = (typeof window.isSecureContext === 'undefined') ? true : !!window.isSecureContext;
      if (!topLevel || !secure) { done = true; return true; }   // never attach in a frame / insecure page
      var mc = currentMC();
      if (!mc || typeof mc.registerTool !== 'function') return false;   // modelContext not injected yet: retry
      attach(mc);
      done = true;   // it appeared and we attempted the attach (idempotent); stop the retry schedule
      return true;
    }
    try {
      if (!tryAttachNow()) {
        var onEvent = function () { tryAttachNow(); };
        if (window.document && window.document.addEventListener) { window.document.addEventListener('DOMContentLoaded', onEvent); }
        if (window.addEventListener) { window.addEventListener('load', onEvent); }
        var intervals = [500, 500, 1000, 2000, 4000, 8000, 15000];   // cumulative about 31s, then give up
        var scheduleRetry = function (i) {
          if (done || i >= intervals.length || typeof window.setTimeout !== 'function') return;
          window.setTimeout(function () { if (!tryAttachNow()) scheduleRetry(i + 1); }, intervals[i]);
        };
        scheduleRetry(0);
      }
    } catch (e) { /* scheduling APIs missing (e.g. a bare test window): the manual attach path still works */ }
  }
})();
